import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChainsTheme } from '../../context/ChainsThemeContext';
import { 
  BarChart3, Plus, Filter, Search, Download, 
  TrendingUp, DollarSign, Clock, CheckCircle, XCircle, 
  AlertTriangle, ArrowUpRight, ArrowDownRight, RefreshCw,
  Eye, Edit, MoreVertical, Calendar, MapPin,
  Truck, Globe, Shield, FileText, PieChart,
  Activity, ShoppingCart, CreditCard, Receipt, Thermometer,
  Droplets, Zap, Box, Palette, Layers, Cube, Archive,
  Target, TrendingDown, Users, Package, Settings, Warehouse
} from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ComposedChart, ScatterChart, Scatter,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedReport, setSelectedReport] = useState('all');
  const [timeRange, setTimeRange] = useState('30d');
  const { isDarkMode } = useChainsTheme();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data for reports and analytics
  const kpiData = [
    { 
      name: 'Total Revenue', 
      value: 'SAR 2.8M', 
      change: '+12.5%', 
      trend: 'up', 
      icon: DollarSign, 
      color: 'blue',
      target: 'SAR 3.0M',
      progress: 93.3
    },
    { 
      name: 'Order Fulfillment', 
      value: '96.8%', 
      change: '+2.1%', 
      trend: 'up', 
      icon: CheckCircle, 
      color: 'green',
      target: '98%',
      progress: 98.8
    },
    { 
      name: 'Inventory Turnover', 
      value: '8.5x', 
      change: '+0.8x', 
      trend: 'up', 
      icon: RefreshCw, 
      color: 'purple',
      target: '10x',
      progress: 85
    },
    { 
      name: 'Cost Reduction', 
      value: 'SAR 450K', 
      change: '+15.2%', 
      trend: 'up', 
      icon: TrendingUp, 
      color: 'emerald',
      target: 'SAR 500K',
      progress: 90
    },
    { 
      name: 'Supplier Performance', 
      value: '94.2%', 
      change: '+1.8%', 
      trend: 'up', 
      icon: Users, 
      color: 'cyan',
      target: '95%',
      progress: 99.2
    },
    { 
      name: 'Warehouse Efficiency', 
      value: '91.7%', 
      change: '+3.2%', 
      trend: 'up', 
      icon: Warehouse, 
      color: 'orange',
      target: '92%',
      progress: 99.7
    }
  ];

  const performanceData = [
    { month: 'Jan', revenue: 1250000, orders: 1250, costs: 850000, efficiency: 88.5 },
    { month: 'Feb', revenue: 1380000, orders: 1420, costs: 920000, efficiency: 91.2 },
    { month: 'Mar', revenue: 1520000, orders: 1580, costs: 980000, efficiency: 93.8 },
    { month: 'Apr', revenue: 1680000, orders: 1750, costs: 1050000, efficiency: 94.2 },
    { month: 'May', revenue: 1820000, orders: 1920, costs: 1120000, efficiency: 95.1 },
    { month: 'Jun', revenue: 1950000, orders: 2080, costs: 1180000, efficiency: 96.8 },
  ];

  const supplierPerformanceData = [
    { name: 'TechCorp Solutions', delivery: 98, quality: 95, cost: 92, communication: 96, overall: 95.3 },
    { name: 'Global Logistics Co.', delivery: 95, quality: 92, cost: 88, communication: 94, overall: 92.3 },
    { name: 'Premium Suppliers', delivery: 96, quality: 94, cost: 90, communication: 95, overall: 93.8 },
    { name: 'FastTrack Express', delivery: 92, quality: 88, cost: 85, communication: 90, overall: 88.8 },
    { name: 'Quality Materials Ltd.', delivery: 89, quality: 91, cost: 87, communication: 88, overall: 88.8 }
  ];

  const categoryData = [
    { name: 'IT Equipment', value: 35, color: '#3B82F6' },
    { name: 'Logistics', value: 25, color: '#10B981' },
    { name: 'Office Supplies', value: 20, color: '#F59E0B' },
    { name: 'Transportation', value: 15, color: '#EF4444' },
    { name: 'Raw Materials', value: 5, color: '#8B5CF6' }
  ];

  const reportTemplates = [
    { 
      id: 'RPT-001', 
      name: 'Monthly Supply Chain Performance', 
      type: 'monthly', 
      lastGenerated: '2024-01-15', 
      status: 'scheduled',
      description: 'Comprehensive monthly overview of supply chain KPIs and performance metrics',
      sections: ['Revenue Analysis', 'Cost Analysis', 'Efficiency Metrics', 'Supplier Performance'],
      recipients: ['management@company.com', 'operations@company.com']
    },
    { 
      id: 'RPT-002', 
      name: 'Vendor Performance Report', 
      type: 'quarterly', 
      lastGenerated: '2024-01-10', 
      status: 'completed',
      description: 'Detailed analysis of vendor performance, delivery times, and quality metrics',
      sections: ['Delivery Performance', 'Quality Metrics', 'Cost Analysis', 'Recommendations'],
      recipients: ['procurement@company.com', 'vendors@company.com']
    },
    { 
      id: 'RPT-003', 
      name: 'Inventory Optimization Report', 
      type: 'weekly', 
      lastGenerated: '2024-01-14', 
      status: 'scheduled',
      description: 'Weekly inventory levels, turnover rates, and optimization recommendations',
      sections: ['Stock Levels', 'Turnover Analysis', 'Reorder Points', 'Optimization Tips'],
      recipients: ['warehouse@company.com', 'inventory@company.com']
    },
    { 
      id: 'RPT-004', 
      name: 'Cost Analysis Report', 
      type: 'monthly', 
      lastGenerated: '2024-01-12', 
      status: 'completed',
      description: 'Monthly cost analysis across all supply chain operations and cost reduction opportunities',
      sections: ['Cost Breakdown', 'Variance Analysis', 'Cost Trends', 'Savings Opportunities'],
      recipients: ['finance@company.com', 'operations@company.com']
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'scheduled': return 'text-blue-400';
      case 'processing': return 'text-orange-400';
      case 'failed': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20';
      case 'scheduled': return 'bg-blue-500/20';
      case 'processing': return 'bg-orange-500/20';
      case 'failed': return 'bg-red-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  const KPICard = ({ kpi }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl bg-${kpi.color}-500/20`}>
          <kpi.icon className={`w-6 h-6 text-${kpi.color}-500`} />
        </div>
        <div className={`flex items-center text-sm ${
          kpi.trend === 'up' ? 'text-green-400' : 'text-red-400'
        }`}>
          {kpi.trend === 'up' ? (
            <ArrowUpRight className="w-4 h-4 mr-1" />
          ) : (
            <ArrowDownRight className="w-4 h-4 mr-1" />
          )}
          {kpi.change}
        </div>
      </div>
      
      <div className="mb-4">
        <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{kpi.value}</p>
        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{kpi.name}</p>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Target</span>
          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{kpi.target}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${kpi.progress}%` }}
            transition={{ duration: 1, delay: 0.2 }}
            className={`h-2 rounded-full ${
              kpi.progress >= 90 ? 'bg-green-500' :
              kpi.progress >= 75 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
          />
        </div>
        <div className="flex justify-between items-center">
          <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Progress</span>
          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{kpi.progress}%</span>
        </div>
      </div>
    </motion.div>
  );

  const ReportTemplateCard = ({ report }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-sm rounded-2xl p-6 hover:shadow-xl transition-all duration-300 ${
        isDarkMode 
          ? 'bg-white/10 border border-white/20' 
          : 'bg-white/70 border border-gray-200/50'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{report.name}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBg(report.status)} ${getStatusColor(report.status)}`}>
              {report.status}
            </span>
          </div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{report.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Type</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{report.type}</p>
        </div>
        <div>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Last Generated</p>
          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{report.lastGenerated}</p>
        </div>
      </div>

      <div className="mb-4">
        <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'} mb-2`}>Sections</p>
        <div className="flex flex-wrap gap-1">
          {report.sections.map((section, index) => (
            <span
              key={index}
              className={`px-2 py-1 rounded-full text-xs ${
                isDarkMode ? 'bg-white/10 text-white/80' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {section}
            </span>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-white/10">
        <div className="text-sm">
          <p className={`${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Recipients: {report.recipients.length}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Eye className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Download className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
          <button className={`p-2 rounded-lg transition-colors ${
            isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}>
            <Edit className={`w-4 h-4 ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="p-6 space-y-8 h-full overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
                      <h1 className={`text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Reports & Analytics</h1>
          <p className={`text-lg ${isDarkMode ? 'text-white/60' : 'text-gray-700'}`}>Comprehensive supply chain reporting and KPI analytics</p>
        </div>
        <div className="flex items-center space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`px-4 py-2 backdrop-blur-sm border rounded-xl transition-all ${
              isDarkMode 
                ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
                : 'bg-white/50 border-gray-200/50 text-gray-700 hover:bg-white/70'
            }`}
          >
            <Download className="w-5 h-5 mr-2 inline" />
            Export All
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all"
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            Create Report
          </motion.button>
        </div>
      </motion.div>

      {/* KPI Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
        {kpiData.map((kpi, index) => (
          <motion.div
            key={kpi.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <KPICard kpi={kpi} />
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trends */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Performance Trends</h3>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Revenue</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>Efficiency</span>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
              <XAxis dataKey="month" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
              <Bar dataKey="revenue" fill="#3B82F6" fillOpacity={0.3} />
              <Line type="monotone" dataKey="efficiency" stroke="#10B981" strokeWidth={3} />
            </ComposedChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Supplier Performance Radar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className={`backdrop-blur-sm rounded-2xl p-6 ${
            isDarkMode 
              ? 'bg-white/10 border border-white/20' 
              : 'bg-white/70 border border-gray-200/50'
          }`}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Supplier Performance</h3>
            <button className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}>View All</button>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={supplierPerformanceData.slice(0, 3)}>
              <PolarGrid stroke={isDarkMode ? '#ffffff20' : '#e5e7eb'} />
              <PolarAngleAxis 
                dataKey="name" 
                tick={{ fill: isDarkMode ? '#ffffff60' : '#6b7280', fontSize: 12 }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fill: isDarkMode ? '#ffffff60' : '#6b7280', fontSize: 10 }}
              />
              <Radar
                name="Overall Performance"
                dataKey="overall"
                stroke="#3B82F6"
                fill="#3B82F6"
                fillOpacity={0.3}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: isDarkMode ? '#ffffff' : '#000000'
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex items-center space-x-1 p-1 backdrop-blur-sm rounded-xl border border-white/20">
        {[
          { id: 'overview', label: 'Overview', count: kpiData.length },
          { id: 'reports', label: 'Reports', count: reportTemplates.length },
          { id: 'analytics', label: 'Analytics', count: 12 },
          { id: 'custom', label: 'Custom Reports', count: 5 }
        ].map((tab) => (
          <motion.button
            key={tab.id}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg'
                : isDarkMode 
                  ? 'text-white/70 hover:text-white hover:bg-white/10'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <span>{tab.label}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
              activeTab === tab.id ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
            }`}>
              {tab.count}
            </span>
          </motion.button>
        ))}
      </div>

      {/* Content based on active tab */}
      <AnimatePresence mode="wait">
        {activeTab === 'reports' && (
          <motion.div
            key="reports"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            {reportTemplates.map((report, index) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <ReportTemplateCard report={report} />
              </motion.div>
            ))}
          </motion.div>
        )}

        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-6"
          >
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Reports</h3>
              {reportTemplates.slice(0, 2).map((report, index) => (
                <motion.div
                  key={report.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <ReportTemplateCard report={report} />
                </motion.div>
              ))}
            </div>
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Top Performers</h3>
              <div className="space-y-4">
                {kpiData.slice(0, 3).map((kpi, index) => (
                  <motion.div
                    key={kpi.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`backdrop-blur-sm rounded-xl p-4 ${
                      isDarkMode 
                        ? 'bg-white/10 border border-white/20' 
                        : 'bg-white/70 border border-gray-200/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg bg-${kpi.color}-500/20`}>
                          <kpi.icon className={`w-5 h-5 text-${kpi.color}-500`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{kpi.name}</p>
                          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-gray-600'}`}>{kpi.value}</p>
                        </div>
                      </div>
                      <div className={`text-sm ${
                        kpi.trend === 'up' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {kpi.change}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'analytics' && (
          <motion.div
            key="analytics"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Cost Analysis</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
                    <XAxis dataKey="month" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
                    <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: isDarkMode ? '#ffffff' : '#000000'
                      }}
                    />
                    <Area type="monotone" dataKey="costs" stroke="#EF4444" fill="#EF4444" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Order Volume</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#ffffff20' : '#f0f0f0'} />
                    <XAxis dataKey="month" stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
                    <YAxis stroke={isDarkMode ? '#ffffff60' : '#6b7280'} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: isDarkMode ? '#ffffff' : '#000000'
                      }}
                    />
                    <Bar dataKey="orders" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className={`backdrop-blur-sm rounded-2xl p-6 ${
                  isDarkMode 
                    ? 'bg-white/10 border border-white/20' 
                    : 'bg-white/70 border border-gray-200/50'
                }`}
              >
                <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Category Distribution</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsPieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: isDarkMode ? '#ffffff' : '#000000'
                      }}
                    />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reports; 